import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { Plus, MessageSquare, Settings, User, PanelLeftClose, PanelLeft, Trash2, Loader2, Pencil, Check, X, LogOut, AlertTriangle, ChevronDown, Star, MoreHorizontal, ArrowLeft, Folder, Search } from 'lucide-react';
import { useTranslation } from '../i18n';
import { ChatSession, PlcBrand } from '../types';
import { appLogoUrl } from '../assets';
import { hasAdminConsoleAccess } from '../services/auth';
import { loadTenantQuota, type TenantQuotaInfo } from '../services/admin';
import { messageHasCompletedProjectSummary, sessionHasAwaitingPlanReview, sessionHasCompletedOutput } from '../utils/planReviewState';
import { formatSessionStatusLabel } from '../utils/sessionStatusLabel';
import type { ProjectRecord } from '../services/projectStore';
import { useValidationActivityStore } from '../stores/validationActivityStore';

// Projects / Starred are capped in the main view so the time-grouped chat list
// stays above the fold. Longer lists are reachable via "Show all".
const SECTION_PREVIEW_LIMIT = 4;

interface SidebarSecureProps {
  isOpen: boolean;
  onToggle: () => void;
  onHome: () => void;
  onNewChat: () => void;
  onOpenSettings: () => void;
  sessions: ChatSession[];
  currentSessionId: string | null;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
  onRenameSession: (id: string, title: string) => void;
  onStarSession: (id: string) => void;
  authUser?: {
    name?: string;
    email?: string;
    phone?: string;
    role?: string;
    permissions?: string[];
  } | null;
  onOpenAdmin?: () => void;
  onOpenAccount?: () => void;
  onLogout?: () => void;
  // Project-related props
  projects: ProjectRecord[];
  sidebarView: 'main' | 'project';
  activeProjectId: string | null;
  activeProjectName: string;
  projectSessions: ChatSession[];
  starredSessions: ChatSession[];
  onBackFromProject: () => void;
  onNewProjectChat: () => void;
  onOpenCreateProject: () => void;
  onEnterProject: (projectId: string) => void;
  onDeleteProject: (projectId: string) => void;
  onRenameProject: (projectId: string, name: string) => void;
  viewedSessionIds: Set<string>;
}

type TimeGroup = {
  label: string;
  sessions: ChatSession[];
};

const groupSessionsByTime = (sessions: ChatSession[], t: (key: string) => string): TimeGroup[] => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const groups: {
    today: ChatSession[];
    yesterday: ChatSession[];
    sevenDays: ChatSession[];
    thirtyDays: ChatSession[];
    older: Record<string, ChatSession[]>;
  } = {
    today: [],
    yesterday: [],
    sevenDays: [],
    thirtyDays: [],
    older: {},
  };

  sessions.forEach((session) => {
    const sessionDate = new Date(session.updatedAt);
    const sessionDateOnly = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());

    if (sessionDateOnly.getTime() === today.getTime()) groups.today.push(session);
    else if (sessionDateOnly.getTime() === yesterday.getTime()) groups.yesterday.push(session);
    else if (sessionDateOnly >= sevenDaysAgo) groups.sevenDays.push(session);
    else if (sessionDateOnly >= thirtyDaysAgo) groups.thirtyDays.push(session);
    else {
      const monthKey = `${sessionDate.getFullYear()}-${String(sessionDate.getMonth() + 1).padStart(2, '0')}`;
      if (!groups.older[monthKey]) groups.older[monthKey] = [];
      groups.older[monthKey].push(session);
    }
  });

  const result: TimeGroup[] = [];
  if (groups.today.length > 0) result.push({ label: t('Today'), sessions: groups.today });
  if (groups.yesterday.length > 0) result.push({ label: t('Yesterday'), sessions: groups.yesterday });
  if (groups.sevenDays.length > 0) result.push({ label: t('7 Days'), sessions: groups.sevenDays });
  if (groups.thirtyDays.length > 0) result.push({ label: t('30 Days'), sessions: groups.thirtyDays });

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  Object.keys(groups.older)
    .sort((a, b) => b.localeCompare(a))
    .forEach((monthKey) => {
      const [year, month] = monthKey.split('-').map(Number);
      result.push({ label: `${monthNames[month - 1]} ${year}`, sessions: groups.older[monthKey] });
    });

  return result;
};

const isQuotaLimitedPlan = (plan?: string) => {
  const normalized = (plan || '').trim().toLowerCase().replace(/[_\s]+/g, '-');
  return normalized === 'free' || normalized === 'freetest' || normalized === 'free-test' || normalized === 'trial';
};

const isEmailLike = (value?: string) => Boolean(value?.includes('@'));

const isPlanLikeName = (value?: string) => /^vip[\s_-]*\d+$/i.test((value || '').trim());

const getEmailPrefix = (email?: string) => (email || '').split('@')[0]?.trim() || '';

const getPreferredDisplayName = (authUser?: SidebarSecureProps['authUser']) => {
  const accountName = authUser?.name?.trim();
  if (accountName && !isEmailLike(accountName) && !isPlanLikeName(accountName)) {
    return accountName;
  }
  return getEmailPrefix(authUser?.email) || authUser?.phone?.trim() || '';
};


const normalizeSessionStatusLabel = (status?: string): string => {
  return (status || '').trim().replace(/\s+/g, ' ');
};

const getSessionDisplayStatusCandidate = (session: ChatSession): string => {
  const record = session as unknown as Record<string, unknown>;
  const displayStatus = typeof record.displayStatus === 'string' ? record.displayStatus : '';
  const runtimeDisplayStatus = typeof record.runtimeDisplayStatus === 'string' ? record.runtimeDisplayStatus : '';
  const stableStatus = typeof record.stableStatus === 'string' ? record.stableStatus : '';
  return normalizeSessionStatusLabel(displayStatus || runtimeDisplayStatus || stableStatus || session.status);
};

const getSessionStatusStorageKey = (sessionId: string) => `realplc_session_status_label_${sessionId}`;

function readStoredSessionStatusLabel(sessionId: string): string {
  if (typeof window === 'undefined') return '';
  try {
    return localStorage.getItem(getSessionStatusStorageKey(sessionId)) || '';
  } catch {
    return '';
  }
}

function writeStoredSessionStatusLabel(sessionId: string, label: string) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(getSessionStatusStorageKey(sessionId), label);
  } catch {
    // Ignore storage quota / private mode failures. The in-memory cache still works.
  }
}

const SidebarSecureComponent: React.FC<SidebarSecureProps> = ({
  isOpen,
  onToggle,
  onHome,
  onNewChat,
  onOpenSettings,
  sessions,
  currentSessionId,
  onSelectSession,
  onDeleteSession,
  onRenameSession,
  onStarSession,
  authUser,
  onOpenAdmin,
  onLogout,
  projects,
  sidebarView,
  activeProjectName,
  projectSessions,
  starredSessions,
  onBackFromProject,
  onNewProjectChat,
  onOpenCreateProject,
  onEnterProject,
  onDeleteProject,
  onRenameProject,
  viewedSessionIds,
}) => {
  const { t, language } = useTranslation();
  const [userName, setUserName] = useState('Authenticated User');
  const [userAvatar, setUserAvatar] = useState('');
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [logoError, setLogoError] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isQuotaDetailsOpen, setIsQuotaDetailsOpen] = useState(false);
  const [tenantQuota, setTenantQuota] = useState<TenantQuotaInfo | null>(null);
  const quotaDetailsRef = React.useRef<HTMLDivElement>(null);
  const settingsMenuRef = React.useRef<HTMLDivElement>(null);
  const settingsMenuContainerRef = React.useRef<HTMLDivElement>(null);
  const previousStreamingCountRef = useRef(0);
  const [openSessionMenuId, setOpenSessionMenuId] = useState<string | null>(null);
  const sessionMenuRef = useRef<HTMLDivElement>(null);
  const sessionStatusLabelCacheRef = useRef<Record<string, string>>({});
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(new Set());
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [showAllStarred, setShowAllStarred] = useState(false);

  // Project-related state
  const [projectRenameId, setProjectRenameId] = useState<string | null>(null);
  const [projectRenameDraft, setProjectRenameDraft] = useState('');
  const [openProjectMenuId, setOpenProjectMenuId] = useState<string | null>(null);
  const projectMenuRef = useRef<HTMLDivElement>(null);

  const toggleGroupCollapse = useCallback((groupId: string) => {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const refreshTenantQuota = useCallback(() => {
    loadTenantQuota()
      .then(setTenantQuota)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        settingsMenuRef.current && !settingsMenuRef.current.contains(event.target as Node) &&
        settingsMenuContainerRef.current && !settingsMenuContainerRef.current.contains(event.target as Node)
      ) {
        setIsSettingsMenuOpen(false);
      }
    };
    if (isSettingsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isSettingsMenuOpen]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sessionMenuRef.current && !sessionMenuRef.current.contains(event.target as Node)) {
        setOpenSessionMenuId(null);
      }
    };
    if (openSessionMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openSessionMenuId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (projectMenuRef.current && !projectMenuRef.current.contains(event.target as Node)) {
        setOpenProjectMenuId(null);
      }
    };
    if (openProjectMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openProjectMenuId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quotaDetailsRef.current && !quotaDetailsRef.current.contains(event.target as Node)) {
        setIsQuotaDetailsOpen(false);
      }
    };
    if (isQuotaDetailsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isQuotaDetailsOpen]);

  useEffect(() => {
    refreshTenantQuota();
  }, [refreshTenantQuota]);

  useEffect(() => {
    const handleQuotaUpdated = (event: Event) => {
      const detail = (event as CustomEvent<TenantQuotaInfo>).detail;
      if (detail) setTenantQuota(detail);
    };
    window.addEventListener('tenant_quota_updated', handleQuotaUpdated);
    return () => window.removeEventListener('tenant_quota_updated', handleQuotaUpdated);
  }, []);

  // Refresh user profile on settings changes
  useEffect(() => {
    const handleSettingsUpdated = () => {
      const userKey = authUser?.email || 'default';
      setUserAvatar(localStorage.getItem(`user_avatar_${userKey}`) || '');
      const savedUserName = localStorage.getItem(`user_name_${userKey}`);
      if (savedUserName && !isEmailLike(savedUserName) && !isPlanLikeName(savedUserName)) {
        setUserName(savedUserName);
      } else {
        setUserName(getPreferredDisplayName(authUser) || t('Authenticated User'));
      }
    };
    window.addEventListener('settings_updated', handleSettingsUpdated);
    return () => window.removeEventListener('settings_updated', handleSettingsUpdated);
  }, [authUser, t]);

  useEffect(() => {
    const streamingCount = sessions.filter((session) => session.isStreaming && !isStreamingStale(session)).length;
    if (previousStreamingCountRef.current > 0 && streamingCount === 0) {
      refreshTenantQuota();
    }
    previousStreamingCountRef.current = streamingCount;
  }, [refreshTenantQuota, sessions]);

  /**
   * The turns still running a post-generation validation.
   *
   * `session.isStreaming` cannot answer this: the CODESYS closed loop starts
   * after the SSE stream has closed, so by the time it runs the session has
   * already reported itself idle. Read from the store the turn publishes to —
   * see `validationActivityStore`.
   */
  const validationActivity = useValidationActivityStore(state => state.activity);
  const validatingMessageIds = useMemo(
    () => new Set(
      Object.entries(validationActivity)
        .filter(([, item]) => item.busy)
        .map(([messageId]) => messageId),
    ),
    [validationActivity],
  );
  const isValidating = useCallback(
    (session: ChatSession) => validatingMessageIds.size > 0
      && Boolean(session.messages?.some(message => validatingMessageIds.has(message.id))),
    [validatingMessageIds],
  );

  const STALE_STREAMING_MS = 2 * 60 * 1000; // 2 min inactivity threshold when lastStreamingActivityAt is known

  const hasActiveRepair = useCallback((session: ChatSession) => {
    const lastMsg = session.messages[session.messages.length - 1] as any;
    const repairStatus = lastMsg?.repairAppendix?.status;
    const phase = lastMsg?.progressState?.phase;
    const stage = lastMsg?.progressState?.stage;
    return repairStatus === 'repairing'
      || phase === 'repairing'
      || (stage === 'repair' && phase !== 'completed' && phase !== 'failed');
  }, []);

  const isStreamingStale = useCallback((session: ChatSession) => {
    if (!session.isStreaming) return false;
    if (sessionHasAwaitingPlanReview(session)) return false;
    const lastMsg = session.messages[session.messages.length - 1] as any;
    if (hasActiveRepair(session)) return false;
    if (lastMsg?.role === 'assistant' && lastMsg?.clarificationChecklist?.length > 0) return false;
    const lastActivity = session.lastStreamingActivityAt;
    if (lastActivity == null) {
      if (session.messages.some(m => (m as any).isStreaming)) return false;
      if (sessionHasCompletedOutput(session.messages)) return true;
      return false;
    }
    return Date.now() - lastActivity > STALE_STREAMING_MS;
  }, [hasActiveRepair]);

  useEffect(() => {
    const userKey = authUser?.email || 'default';
    const savedUserName = localStorage.getItem(`user_name_${userKey}`);
    setUserName(
      savedUserName && !isEmailLike(savedUserName) && !isPlanLikeName(savedUserName)
        ? savedUserName
        : getPreferredDisplayName(authUser) || t('Authenticated User'),
    );
    setUserAvatar(localStorage.getItem(`user_avatar_${userKey}`) || '');
  }, [authUser, t]);

  const roleLabel = useMemo(() => {
    if (authUser?.role === 'super_admin') return t('Platform Admin');
    if (authUser?.role === 'admin') return t('Organization Admin');
    if (authUser?.role === 'user') return t('Authenticated User');
    return t('Viewer');
  }, [authUser?.role, t]);

  const formatTime = useCallback((timestamp: number) => {
    const date = new Date(timestamp);
    const diff = Date.now() - date.getTime();
    const locale = language === 'Chinese' ? 'zh-CN' : 'en-US';
    if (diff < 24 * 60 * 60 * 1000) return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    if (diff < 48 * 60 * 60 * 1000) return t('Yesterday');
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  }, [language, t]);

  const formatQuotaDateTime = useCallback((timestamp?: number | null) => {
    if (!timestamp) return language === 'Chinese' ? '暂无' : 'Not available';
    const locale = language === 'Chinese' ? 'zh-CN' : 'en-US';
    return new Date(timestamp * 1000).toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, [language, t]);

  const beginRenameSession = useCallback((session: ChatSession) => {
    setEditingSessionId(session.id);
    setDraftTitle(session.title);
  }, []);

  const cancelRenameSession = useCallback(() => {
    setEditingSessionId(null);
    setDraftTitle('');
  }, []);

  const submitRenameSession = useCallback((sessionId: string) => {
    const normalizedTitle = draftTitle.replace(/\s+/g, ' ').trim();
    if (normalizedTitle) onRenameSession(sessionId, normalizedTitle);
    cancelRenameSession();
  }, [cancelRenameSession, draftTitle, onRenameSession]);

  // Independent chats only (no projectId) for time-grouped display
  const independentSessions = useMemo(() => {
    // Main history should show personal / non-project chats only. Project chats
    // live under their project, matching ChatGPT / Claude project behavior.
    // The active project list is also used as a transient guard while remote
    // projectId persistence is still catching up.
    const activeProjectSessionIds = new Set(projectSessions.map((session) => session.id));
    return sessions.filter((session) => !session.projectId && !activeProjectSessionIds.has(session.id));
  }, [projectSessions, sessions]);

  // Session search. Engineers accumulate hundreds of conversations ("the
  // conveyor one from last month"); without this the history is write-only.
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const matchesQuery = useCallback(
    (session: ChatSession) => !normalizedQuery
      || (session.title || '').toLowerCase().includes(normalizedQuery),
    [normalizedQuery],
  );

  const searchResults = useMemo(
    () => (normalizedQuery ? sessions.filter(matchesQuery) : []),
    [matchesQuery, normalizedQuery, sessions],
  );

  const timeGroups = useMemo(
    () => groupSessionsByTime(independentSessions, t),
    [independentSessions, t],
  );

  const visibleProjects = showAllProjects ? projects : projects.slice(0, SECTION_PREVIEW_LIMIT);
  const visibleStarredSessions = showAllStarred ? starredSessions : starredSessions.slice(0, SECTION_PREVIEW_LIMIT);

  const quotaCopy = useMemo(() => ({
    tokenUsage: language === 'Chinese' ? 'Token 用量' : 'Token usage',
    available: language === 'Chinese' ? '可用' : 'left',
    quotaLow: language === 'Chinese' ? '额度偏低' : 'Quota running low',
    quotaUnset: language === 'Chinese' ? '配额未设置' : 'Quota not set',
    quotaUnsetDetail: language === 'Chinese' ? '当前组织还没有配置月度 Token 限额。' : 'This organization does not have a monthly token quota configured.',
    monthlyReset: language === 'Chinese' ? '月度重置' : 'Monthly reset',
    rollingWindow: language === 'Chinese' ? '24小时窗口' : '24h window',
    recoversAt: language === 'Chinese' ? '恢复' : 'Recovers',
  }), [language]);

  const formatPlanLabel = useCallback((plan: string) => {
    const normalized = plan.trim().toLowerCase().replace(/[_\s]+/g, '-');
    if (normalized === 'freetest' || normalized === 'free-test') return 'Freetest';
    if (normalized === 'trial') return 'Trial';
    return 'Free';
  }, []);

	  const getStableSessionStatusLabel = useCallback((session: ChatSession) => {
	    // The session config names the platform directly, so it does not matter
	    // whether the stored status string was written by this browser or by the
	    // gateway (which formats it from the lowercase vendor slug).
	    const fromConfig = formatSessionStatusLabel(session.sessionConfig);
	    if (fromConfig) return fromConfig;

	    const normalized = getSessionDisplayStatusCandidate(session);
	    const cached = sessionStatusLabelCacheRef.current[session.id] || readStoredSessionStatusLabel(session.id);
	    const activelyRunning = Boolean(session.isStreaming && !isStreamingStale(session));

	    const abbreviateStructuredText = (label: string): string => {
	      if (!label) return label;
	      const brand = session.sessionConfig?.brand;
	      const abbr = brand === PlcBrand.Siemens ? 'SCL' : 'ST';
	      return label
	        .replace(/\bStructured\s*Text\b/gi, abbr)
	        .replace(/\bSTRUCT\b/g, abbr)
	        .replace(/\s*\((?:SCL\/ST|ST\/SCL|IL\/STL|ASCII[^)]*)\)/gi, '');
	    };

	    if (activelyRunning && normalized) {
	      const abbreviated = abbreviateStructuredText(normalized);
	      sessionStatusLabelCacheRef.current[session.id] = abbreviated;
	      writeStoredSessionStatusLabel(session.id, abbreviated);
	      return abbreviated;
	    }

	    if (cached) {
	      const abbreviated = abbreviateStructuredText(cached);
	      sessionStatusLabelCacheRef.current[session.id] = abbreviated;
	      return abbreviated;
	    }

	    return abbreviateStructuredText(normalized);
	  }, [isStreamingStale]);
  const closeSessionMenu = useCallback(() => {
    setOpenSessionMenuId(null);
  }, []);

  const closeProjectMenu = useCallback(() => {
    setOpenProjectMenuId(null);
  }, []);

  // ── Session entry renderer (reused in main view and project sub-view) ──
  const renderSessionEntry = useCallback((session: ChatSession) => {
    const isEditing = editingSessionId === session.id;
    const isCurrent = currentSessionId === session.id;
    const hasPlanWaiting = sessionHasAwaitingPlanReview(session);
    const lastMsg = session.messages[session.messages.length - 1] as any;
    const hasClarifyWaiting = !session.isStreaming
      && lastMsg?.role === 'assistant'
      && !messageHasCompletedProjectSummary(lastMsg)
      && (lastMsg?.clarificationChecklist?.length > 0);
    const hasProjectOutput = sessionHasCompletedOutput(session.messages);
    const sessionValidating = isValidating(session);
    const statusLabel = getStableSessionStatusLabel(session);
    const isSessionMenuOpen = openSessionMenuId === session.id;

    return (
      <div key={session.id} data-session-row={session.id} className="group relative flex items-center">
        {isEditing ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitRenameSession(session.id);
            }}
            className={`flex w-full items-center gap-1 rounded-xl border px-3 py-2 text-xs md:text-sm ${isCurrent ? 'border-cyan-300 bg-cyan-100 dark:border-cyan-700/60 dark:bg-cyan-900/30' : 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900'}`}
          >
            <input value={draftTitle} onChange={(event) => setDraftTitle(event.target.value)} autoFocus className="min-w-0 flex-1 bg-transparent text-gray-800 outline-none dark:text-zinc-100" />
            <button type="submit" aria-label={t('Confirm rename')} className="flex-shrink-0 p-1 text-emerald-600 dark:text-emerald-400"><Check size={14} /></button>
            <button type="button" onClick={cancelRenameSession} aria-label={t('Cancel')} className="flex-shrink-0 p-1 text-gray-500 dark:text-zinc-400"><X size={14} /></button>
          </form>
        ) : (
          <div className="flex w-full items-center">
                <button onClick={() => onSelectSession(session.id)} className={`relative flex flex-1 flex-col gap-0.5 truncate rounded-xl px-3 py-2 pr-10 text-left text-[13px] leading-[18px] transition-all ${isCurrent ? 'bg-cyan-100 text-cyan-800 shadow-sm dark:bg-cyan-900/30 dark:text-cyan-100' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800 dark:text-zinc-400 dark:hover:bg-zinc-800/50 dark:hover:text-zinc-200'}`}>
              {isCurrent ? <div className="absolute bottom-1 left-0 top-1 w-1 rounded-full bg-cyan-500 dark:bg-cyan-400" /> : null}
              <div className="flex w-full items-center gap-2 overflow-hidden">
                {(() => {
                  if (isStreamingStale(session)) return <AlertTriangle size={12} className="flex-shrink-0 text-amber-500" />;
                  if (session.isStreaming) return <Loader2 size={12} className="flex-shrink-0 animate-spin text-cyan-500" />;
                  // Still working, just not on the model: the closed loop is the
                  // longest single wait in a turn and used to show nothing here.
                  if (sessionValidating) return <Loader2 size={12} className="flex-shrink-0 animate-spin text-cyan-500" />;
                  return <MessageSquare size={12} className="flex-shrink-0 text-gray-400 dark:text-zinc-500" />;
                })()}
                <span className={`min-w-0 truncate font-semibold ${isCurrent ? 'text-cyan-700 dark:text-cyan-200' : 'font-medium'}`}>{session.title}</span>
                {(() => {
                  if (session.isStreaming && !isStreamingStale(session)) return <span className="h-1.5 w-1.5 rounded-full bg-cyan-500 animate-pulse flex-shrink-0" />;
                  if (isStreamingStale(session)) return <span className="h-1.5 w-1.5 rounded-full bg-red-500 flex-shrink-0" />;
                  if (sessionValidating) return <span className="h-1.5 w-1.5 rounded-full bg-cyan-500 animate-pulse flex-shrink-0" />;
                  if ((hasPlanWaiting || hasClarifyWaiting) && !hasProjectOutput) return <span className="h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />;
                  if (hasProjectOutput && !viewedSessionIds.has(session.id) && !isCurrent) return <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 flex-shrink-0" />;
                  return null;
                })()}
                {sessionValidating ? (
                  <span className="ml-auto flex-shrink-0 text-[10px] text-cyan-500/70">{t('Validating')}</span>
                ) : (hasPlanWaiting || hasClarifyWaiting) && !hasProjectOutput ? (
                  <span className="flex-shrink-0 text-cyan-400/60 text-[10px] ml-auto">{t('Awaiting Confirmation')}</span>
                ) : null}
              </div>
                <div className="mt-0.5 flex items-center justify-between px-5 text-[11px] leading-4">
                  {statusLabel ? <span className={`truncate rounded px-1 py-0.5 text-[11px] font-medium ${isCurrent ? 'bg-cyan-200/50 text-cyan-700 dark:bg-cyan-800/50 dark:text-cyan-300' : 'bg-gray-200 text-gray-500 dark:bg-zinc-800 dark:text-zinc-400'}`}>{statusLabel}</span> : <span />}
                <span className={`${isStreamingStale(session) ? 'font-medium text-amber-500' : session.isStreaming || sessionValidating ? 'font-medium text-cyan-500' : isCurrent ? 'text-cyan-600 dark:text-cyan-400' : 'text-gray-400 dark:text-zinc-500'}`}>{formatTime(session.updatedAt)}</span>
              </div>
            </button>
            <div ref={isSessionMenuOpen ? sessionMenuRef : undefined} className={`absolute right-2 top-2 z-20 transition-all ${isSessionMenuOpen || isCurrent ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpenSessionMenuId(prev => prev === session.id ? null : session.id);
                }}
                className={`rounded-lg p-1.5 transition-colors ${isSessionMenuOpen ? 'bg-white text-cyan-600 shadow-sm dark:bg-zinc-800 dark:text-cyan-300' : 'text-gray-400 hover:bg-white/80 hover:text-cyan-600 dark:text-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-cyan-300'}`}
                aria-label={t('More actions')}
                aria-expanded={isSessionMenuOpen}
              >
                <MoreHorizontal size={15} />
              </button>
              {isSessionMenuOpen && (
                <div className="absolute right-0 top-8 min-w-36 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 text-sm shadow-lg dark:border-zinc-700 dark:bg-[#1e1e20]">
                  <button
                    type="button"
                    onClick={(event) => { event.stopPropagation(); closeSessionMenu(); onStarSession(session.id); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-gray-700 transition-colors hover:bg-gray-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  >
                    <Star size={14} className={session.isStarred ? 'fill-amber-400 text-amber-400' : 'text-gray-400'} />
                    <span>{session.isStarred ? t('Unstar') : t('Star')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(event) => { event.stopPropagation(); closeSessionMenu(); beginRenameSession(session); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-gray-700 transition-colors hover:bg-gray-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  >
                    <Pencil size={14} className="text-gray-400 dark:text-zinc-500" />
                    <span>{t('Rename')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(event) => { event.stopPropagation(); closeSessionMenu(); onDeleteSession(session.id); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
                  >
                    <Trash2 size={14} />
                    <span>{t('Delete')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }, [currentSessionId, editingSessionId, draftTitle, openSessionMenuId, isStreamingStale, isValidating, getStableSessionStatusLabel, formatTime, t, onSelectSession, onDeleteSession, onRenameSession, onStarSession, beginRenameSession, cancelRenameSession, submitRenameSession, closeSessionMenu, viewedSessionIds]);

  // A newly created chat is prepended to its time group, which sits below the
  // Projects / Starred sections — scroll it back into view instead of leaving
  // the highlighted row off-screen.
  useEffect(() => {
    if (!isOpen || !currentSessionId) return;
    const row = document.querySelector(`[data-session-row="${currentSessionId}"]`);
    // jsdom has no scrollIntoView, and the row is absent while its group is collapsed.
    if (typeof row?.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
  }, [currentSessionId, isOpen, sidebarView]);

  const isProjectsCollapsed = collapsedGroupIds.has('__projects__');

  // ── Sidebar wrapper ──
  return (
    <>
      {isOpen ? <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={onToggle} /> : null}
      <div className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-gray-200 bg-gray-100 transition-all duration-300 ease-in-out dark:border-zinc-800/50 dark:bg-[#111113] md:static ${isOpen ? 'translate-x-0' : '-translate-x-full overflow-hidden md:w-14 md:translate-x-0'}`}>
        {/* ── Collapsed icon strip (desktop only) ── */}
        {!isOpen && (
          <div className="hidden md:flex md:flex-col md:items-center md:h-full md:py-3 md:gap-2">
            <button
              onClick={onToggle}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:bg-gray-200 hover:text-cyan-600 dark:border-zinc-700/50 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-cyan-400"
              title={t('Expand sidebar')}
            >
              <PanelLeft size={18} />
            </button>
            <button
              onClick={onNewChat}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-cyan-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-cyan-400"
              title={t('New Chat')}
            >
              <MessageSquare size={18} />
            </button>
            <button
              onClick={onOpenCreateProject}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-cyan-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-cyan-400"
              title={t('New Project')}
            >
              <Folder size={18} />
            </button>
            <div className="flex-1" />
            <button
              onClick={onOpenSettings}
              className="relative flex h-9 w-9 items-center justify-center rounded-lg hover:bg-gray-200 dark:hover:bg-zinc-800/50"
              title={userName}
            >
              {userAvatar ? (
                <img src={userAvatar} alt={t('Avatar')} className="h-7 w-7 rounded-lg object-cover" />
              ) : (
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-tr from-cyan-600 to-blue-600 text-white">
                  <User size={14} />
                </div>
              )}
            </button>
          </div>
        )}
        {isOpen && (<>
        <div className="flex items-center justify-between border-b border-gray-200 p-3 dark:border-zinc-800/50">
          <button type="button" onClick={onHome} className="flex items-center gap-2 rounded-lg px-1 py-1 text-left transition-colors hover:bg-gray-200/70 dark:hover:bg-zinc-800/60">
            {!logoError ? (
              <img src={appLogoUrl} alt="RealPLC logo" className="h-8 w-8 object-contain" onError={() => setLogoError(true)} />
            ) : null}
            <div className={`bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-lg font-bold text-transparent ${logoError ? '' : 'hidden md:block'}`}>
              {t('SidebarTitle')}
            </div>
          </button>
          <button onClick={onToggle} aria-label={t('Collapse sidebar')} className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:bg-gray-200 hover:text-cyan-600 dark:border-zinc-700/50 dark:hover:bg-cyan-900/20">
            <PanelLeftClose size={16} />
          </button>
        </div>

        {/* ── Project Sub-View ── */}
        {sidebarView === 'project' ? (
          <>
            <div className="p-3">
              <button
                onClick={onBackFromProject}
                className="flex w-full items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
              >
                <ArrowLeft size={16} className="text-gray-400" />
                {t('Back')}
              </button>
            </div>

            <div className="flex items-center gap-2 px-3 pb-2">
              <Folder size={16} className="flex-shrink-0 text-cyan-500" />
                  <span className="truncate text-sm font-medium leading-5 text-gray-800 dark:text-zinc-200">{activeProjectName}</span>
              <button
                type="button"
                onClick={onNewProjectChat}
                className="ml-auto rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-cyan-600 dark:text-zinc-500 dark:hover:bg-zinc-800/60 dark:hover:text-cyan-400"
                aria-label={t('New Chat')}
              >
                <Plus size={16} />
              </button>
            </div>

            <div className="custom-scrollbar flex-1 space-y-0.5 overflow-y-auto p-3 pt-0">
              {projectSessions.length === 0 ? (
                <div className="px-3 py-2 text-sm italic text-gray-500">{t('No recent projects')}</div>
              ) : (
                projectSessions.map((session) => renderSessionEntry(session))
              )}
            </div>
          </>
        ) : (
          /* ── Main View ── */
          <>
            <div className="p-3 space-y-2">
              <button onClick={onNewChat} className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800">
                <MessageSquare size={16} className="text-gray-400" />
                {t('New Chat')}
              </button>
              <button onClick={onOpenCreateProject} className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800">
                <Folder size={16} className="text-gray-400" />
                {t('New Project')}
              </button>

              <div className="relative">
                <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-zinc-500" />
                <input
                  ref={searchInputRef}
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Escape') setSearchQuery(''); }}
                  placeholder={t('Search chats')}
                  aria-label={t('Search chats')}
                  data-session-search
                  className="w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-8 pr-2 text-sm text-gray-700 placeholder-gray-400 transition-colors focus:border-cyan-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:placeholder-zinc-600"
                />
              </div>
            </div>

            <div className="custom-scrollbar flex-1 space-y-2 overflow-y-auto p-3 pt-0">
              {normalizedQuery ? (
                <div>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-zinc-500">
                    {searchResults.length > 0
                      ? `${t('Search results')} · ${searchResults.length}`
                      : t('No matching chats')}
                  </div>
                  <div className="space-y-0.5">
                    {searchResults.map((session) => renderSessionEntry(session))}
                  </div>
                </div>
              ) : (
              <>
              {/* ── Projects Section ── */}
              <div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => toggleGroupCollapse('__projects__')}
                    className="flex flex-1 items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-semibold text-gray-500 transition-colors hover:bg-gray-200 dark:text-zinc-500 dark:hover:bg-zinc-800/60"
                  >
                    <ChevronDown size={12} className={`flex-shrink-0 transition-transform ${isProjectsCollapsed ? '-rotate-90' : ''}`} />
                    <span className="flex-1 text-left">{t('Projects')}</span>
                    <span className="text-zinc-400">({projects.length})</span>
                  </button>
                  <button
                    type="button"
                    onClick={onOpenCreateProject}
                    className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-cyan-600 dark:text-zinc-500 dark:hover:bg-zinc-800/60 dark:hover:text-cyan-400"
                    aria-label={t('New Project')}
                  >
                    <Plus size={16} />
                  </button>
                </div>

                {!isProjectsCollapsed && (
                  <div className="mt-1 space-y-0.5">
                    {projects.length === 0 ? (
                      <div className="px-3 py-2 text-xs italic text-gray-400">{t('No recent projects')}</div>
                    ) : (
                      visibleProjects.map((project) => {
                        const isProjectMenuOpen = openProjectMenuId === project.id;
                        const isRenaming = projectRenameId === project.id;
                        return (
                          <div key={project.id} className="group relative flex items-center">
                            {isRenaming ? (
                              <form
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  const trimmed = projectRenameDraft.trim();
                                  if (trimmed) onRenameProject(project.id, trimmed);
                                  setProjectRenameId(null);
                                }}
                                className="flex w-full items-center gap-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs md:text-sm dark:border-zinc-700 dark:bg-zinc-900"
                              >
                                <input
                                  value={projectRenameDraft}
                                  onChange={(e) => setProjectRenameDraft(e.target.value)}
                                  autoFocus
                                  className="min-w-0 flex-1 bg-transparent text-gray-800 outline-none dark:text-zinc-100"
                                />
                                <button type="submit" aria-label={t('Confirm rename')} className="flex-shrink-0 p-1 text-emerald-600 dark:text-emerald-400"><Check size={14} /></button>
                                <button type="button" onClick={() => setProjectRenameId(null)} aria-label={t('Cancel')} className="flex-shrink-0 p-1 text-gray-500 dark:text-zinc-400"><X size={14} /></button>
                              </form>
                            ) : (
                              <div className="flex w-full items-center">
                                <button
                                  onClick={() => onEnterProject(project.id)}
                            className="flex flex-1 items-center gap-2 truncate rounded-xl px-3 py-2 text-left text-sm leading-5 transition-colors text-gray-600 hover:bg-gray-200 hover:text-gray-800 dark:text-zinc-400 dark:hover:bg-zinc-800/50 dark:hover:text-zinc-200"
                                >
                                  {sessions.some(s => s.projectId === project.id && ((s.isStreaming && !isStreamingStale(s)) || isValidating(s))) ? (
                                    <Loader2 size={13} className="flex-shrink-0 animate-spin text-cyan-500" />
                                  ) : (
                                    <Folder size={13} className="flex-shrink-0 text-gray-400 dark:text-zinc-500" />
                                  )}
                                  <span className="min-w-0 truncate font-medium">{project.name}</span>
                                </button>
                                <div ref={isProjectMenuOpen ? projectMenuRef : undefined} className={`absolute right-2 top-1.5 z-20 ${isProjectMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenProjectMenuId(prev => prev === project.id ? null : project.id);
                                    }}
                                    className={`rounded-lg p-1 transition-colors ${isProjectMenuOpen ? 'bg-white text-cyan-600 shadow-sm dark:bg-zinc-800 dark:text-cyan-300' : 'text-gray-400 hover:bg-white/80 hover:text-cyan-600 dark:text-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-cyan-300'}`}
                                    aria-label={t('Project actions')}
                                    aria-haspopup="menu"
                                    aria-expanded={isProjectMenuOpen}
                                  >
                                    <MoreHorizontal size={14} />
                                  </button>
                                  {isProjectMenuOpen && (
                                    <div className="absolute right-0 top-8 min-w-28 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 text-sm shadow-lg dark:border-zinc-700 dark:bg-[#1e1e20]">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          closeProjectMenu();
                                          setProjectRenameId(project.id);
                                          setProjectRenameDraft(project.name);
                                        }}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-gray-700 transition-colors hover:bg-gray-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
                                      >
                                        <Pencil size={14} className="text-gray-400 dark:text-zinc-500" />
                                        <span>{t('Rename')}</span>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          closeProjectMenu();
                                          onDeleteProject(project.id);
                                        }}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
                                      >
                                        <Trash2 size={14} />
                                        <span>{t('Delete')}</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    {projects.length > SECTION_PREVIEW_LIMIT ? (
                      <button
                        type="button"
                        onClick={() => setShowAllProjects((prev) => !prev)}
                        className="w-full rounded-lg px-3 py-1.5 text-left text-xs text-gray-500 transition-colors hover:bg-gray-200 hover:text-cyan-600 dark:text-zinc-500 dark:hover:bg-zinc-800/60 dark:hover:text-cyan-400"
                      >
                        {showAllProjects ? t('Show less') : `${t('Show all')} (${projects.length})`}
                      </button>
                    ) : null}
                  </div>
                )}
              </div>

              {/* ── Starred Section ── */}
              {starredSessions.length > 0 && (() => {
                const starredGroupId = '__starred__';
                const isStarredCollapsed = collapsedGroupIds.has(starredGroupId);
                return (
                  <div>
                    <button
                      type="button"
                      onClick={() => toggleGroupCollapse(starredGroupId)}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-semibold text-gray-500 transition-colors hover:bg-gray-200 dark:text-zinc-500 dark:hover:bg-zinc-800/60"
                    >
                      <ChevronDown size={12} className={`flex-shrink-0 transition-transform ${isStarredCollapsed ? '-rotate-90' : ''}`} />
                      <span className="flex-1 text-left">{t('Starred')}</span>
                      <span className="text-zinc-400">({starredSessions.length})</span>
                    </button>
                    {!isStarredCollapsed && (
                      <div className="mt-1 space-y-0.5">
                        {visibleStarredSessions.map((session) => renderSessionEntry(session))}
                        {starredSessions.length > SECTION_PREVIEW_LIMIT ? (
                          <button
                            type="button"
                            onClick={() => setShowAllStarred((prev) => !prev)}
                            className="w-full rounded-lg px-3 py-1.5 text-left text-xs text-gray-500 transition-colors hover:bg-gray-200 hover:text-cyan-600 dark:text-zinc-500 dark:hover:bg-zinc-800/60 dark:hover:text-cyan-400"
                          >
                            {showAllStarred ? t('Show less') : `${t('Show all')} (${starredSessions.length})`}
                          </button>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── Recent Chats (time-grouped, independent sessions only) ── */}
              {timeGroups.length === 0 ? (
                <div className="px-3 py-2 text-sm italic text-gray-500">{t('No recent projects')}</div>
              ) : (
                timeGroups.map((group) => {
                  const groupId = `time-${group.label}`;
                  const isCollapsed = collapsedGroupIds.has(groupId);
                  return (
                    <div key={group.label}>
                      <button
                        type="button"
                        onClick={() => toggleGroupCollapse(groupId)}
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-semibold text-gray-500 transition-colors hover:bg-gray-200 dark:text-zinc-500 dark:hover:bg-zinc-800/60"
                      >
                        <ChevronDown size={12} className={`flex-shrink-0 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                        <span className="flex-1 text-left">{group.label}</span>
                        <span className="text-zinc-400">({group.sessions.length})</span>
                      </button>
                      {!isCollapsed && (
                        <div className="mt-1 space-y-0.5">
                          {group.sessions.map((session) => renderSessionEntry(session))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              </>
              )}
            </div>
          </>
        )}

        {/* ── Bottom: Quota + User ── */}
        {tenantQuota && isQuotaLimitedPlan(tenantQuota.plan) ? (() => {
          const hasMonthlyQuota = tenantQuota.monthlyTokens > 0;
          const rawRemaining = hasMonthlyQuota
            ? Math.max(0, Math.min(100, (1 - tenantQuota.usedTokens / tenantQuota.monthlyTokens) * 100))
            : 0;
          const remainingPct = Math.round(rawRemaining * 10) / 10;
          const isLowQuota = hasMonthlyQuota && remainingPct <= 20;
          const statusColor = isLowQuota ? '#ef4444' : remainingPct <= 50 ? '#f97316' : '#22c55e';
          const statusBg = isLowQuota ? 'bg-red-500/10' : remainingPct <= 50 ? 'bg-orange-500/10' : 'bg-emerald-500/10';
          const statusBorder = isLowQuota ? 'border-red-400/20' : remainingPct <= 50 ? 'border-orange-400/20' : 'border-white/10';
          const barGradient = isLowQuota
            ? 'linear-gradient(90deg, #ef4444, #f87171)'
            : remainingPct <= 50
              ? 'linear-gradient(90deg, #f97316, #fb923c)'
              : 'linear-gradient(90deg, #22c55e, #4ade80)';

          return (
            <div ref={quotaDetailsRef} className="relative mx-3 mb-3">
              <button
                type="button"
                aria-expanded={isQuotaDetailsOpen}
                onClick={() => setIsQuotaDetailsOpen((open) => !open)}
                className={`w-full rounded-xl border px-3 py-2 text-left transition-colors hover:bg-white/70 dark:hover:bg-white/[0.04] ${statusBorder} ${statusBg}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="rounded-md bg-white/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-600 dark:bg-white/10 dark:text-zinc-300">
                      {formatPlanLabel(tenantQuota.plan)}
                    </span>
                    <span className="truncate text-[11px] font-semibold text-gray-600 dark:text-zinc-300">
                      {quotaCopy.tokenUsage}
                    </span>
                  </span>
                  <span className="flex flex-shrink-0 items-center gap-1 text-xs font-bold" style={{ color: statusColor }}>
                    {isLowQuota ? (
                      <>
                        <AlertTriangle size={12} aria-hidden="true" />
                        <span className="sr-only">{quotaCopy.quotaLow}</span>
                      </>
                    ) : null}
                    {hasMonthlyQuota ? `${remainingPct.toFixed(1)}% ${quotaCopy.available}` : quotaCopy.quotaUnset}
                    <ChevronDown size={13} className={`text-gray-400 transition-transform dark:text-zinc-500 ${isQuotaDetailsOpen ? 'rotate-180' : ''}`} />
                  </span>
                </div>
                {hasMonthlyQuota ? (
                  <div className="mt-2 h-1 w-full rounded-full bg-black/10 dark:bg-white/10">
                    <div
                      className="h-1 rounded-full transition-all"
                      style={{ width: `${rawRemaining}%`, background: barGradient }}
                    />
                  </div>
                ) : null}
              </button>

              {isQuotaDetailsOpen ? (
                <div className="absolute bottom-full left-0 right-0 z-20 mb-2 rounded-xl border border-gray-200 bg-white p-3 text-[11px] shadow-lg dark:border-zinc-700 dark:bg-[#1e1e20]">
                  {hasMonthlyQuota ? (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-500 dark:text-zinc-400">{quotaCopy.tokenUsage}</span>
                        <span className="font-semibold text-gray-800 dark:text-zinc-100">
                          {tenantQuota.usedTokens.toLocaleString()} / {tenantQuota.monthlyTokens.toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-3 text-gray-500 dark:text-zinc-400">
                        <span>{quotaCopy.monthlyReset}</span>
                        <span className="text-right">{formatQuotaDateTime(tenantQuota.resetAt)}</span>
                      </div>
                      {tenantQuota.rollingWindow ? (
                        <div className="mt-2 border-t border-gray-100 pt-2 dark:border-zinc-800">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-gray-500 dark:text-zinc-400">{quotaCopy.rollingWindow}</span>
                            <span className="font-medium text-gray-700 dark:text-zinc-200">
                              {tenantQuota.rollingWindow.usedTokens.toLocaleString()} / {tenantQuota.rollingWindow.maxTokens.toLocaleString()}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center justify-between gap-3 text-gray-500 dark:text-zinc-400">
                            <span>{quotaCopy.recoversAt}</span>
                            <span className="text-right">{formatQuotaDateTime(tenantQuota.rollingWindow.resetAt)}</span>
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="text-gray-500 dark:text-zinc-400">{quotaCopy.quotaUnsetDetail}</div>
                  )}
                </div>
              ) : null}
            </div>
          );
        })() : null}

        <div className="border-t border-gray-200 bg-gray-50 p-4 backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#161618]/30">
          {hasAdminConsoleAccess(authUser || null) && onOpenAdmin ? (
            <button onClick={onOpenAdmin} className="mb-3 w-full rounded-lg border border-cyan-400/20 bg-cyan-50 dark:bg-cyan-400/10 px-3 py-2 text-sm font-medium text-cyan-700 dark:text-cyan-100 transition-colors hover:bg-cyan-100 dark:hover:bg-cyan-400/15">
              {t('Admin Console')}
            </button>
          ) : null}
          <div className="relative">
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-3 rounded-xl p-2 text-left transition-colors hover:bg-gray-200 dark:hover:bg-zinc-800/50"
              onClick={() => setIsSettingsMenuOpen(!isSettingsMenuOpen)}
              aria-haspopup="menu"
              aria-expanded={isSettingsMenuOpen}
              aria-label={t('Settings')}
            >
<div className="relative" ref={settingsMenuRef}>
                {userAvatar ? (
                  <img src={userAvatar} alt={t('Avatar')} className="h-9 w-9 rounded-lg object-cover" />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-tr from-cyan-600 to-blue-600 text-white">
                    <User size={18} />
                  </div>
                )}
                <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500 dark:border-[#0d0d0f]" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-bold text-gray-700 dark:text-zinc-200">{userName}</span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{roleLabel}</span>
              </div>
              <Settings size={18} className="text-gray-400 transition-colors hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300" />
            </button>
            {isSettingsMenuOpen && (
              <div ref={settingsMenuContainerRef} className="absolute bottom-full left-0 right-0 mb-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-[#1e1e20]">
                <div className="border-b border-gray-100 px-4 py-3 dark:border-zinc-800">
                  <div className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{userName}</div>
                  <div className="text-xs text-gray-500 dark:text-zinc-400">{authUser?.email || authUser?.phone || ''}</div>
                </div>
                <button
                  onClick={() => { setIsSettingsMenuOpen(false); onOpenSettings(); }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                  <Settings size={16} className="text-gray-400 dark:text-zinc-500" />
                  {t('Settings')}
                </button>
                {onLogout && (
                  <button
                    onClick={() => { setIsSettingsMenuOpen(false); onLogout(); }}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
                  >
                    <LogOut size={16} />
                    {t('Sign out now')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        </>)}
      </div>
    </>
  );
};

export const SidebarSecure = memo(SidebarSecureComponent);
